/**
 * Admin Pricing Reference — `/admin/pricing-reference`
 *
 * Renders /app/memory/PRICING.md (the canonical "why we charge what we
 * charge, for whom") inside the admin console. The live SKU editor at
 * `/admin/pricing` is canonical for amounts; this page is canonical
 * for rationale + persona mapping + recent change-log.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon as ArrowLeft, BookOpenIcon as BookOpen, SlidersIcon as Sliders, ArrowSquareOutIcon as ArrowSquareOut, ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";

export default function AdminPricingReference() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing-reference");
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load pricing doc.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const html = useMemo(() => {
    const md = doc?.markdown;
    if (!md) return "";
    try {
      return DOMPurify.sanitize(marked.parse(md, { gfm: true, breaks: false }));
    } catch {
      return "";
    }
  }, [doc]);

  const updatedDate = doc?.mtime_unix
    ? new Date(doc.mtime_unix * 1000).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-pricing-reference-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4"
        data-testid="back-to-admin-link"
      >
        <ArrowLeft size={14} weight="bold" /> Back to admin
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-6 border-b border-[#E5E5E5]">
        <div>
          <div className="flex items-center gap-2 text-[#6B6B6B] text-xs uppercase tracking-[0.18em] mb-2">
            <BookOpen size={14} weight="bold" /> Canonical reference
          </div>
          <h1 className="font-spectral text-3xl sm:text-4xl text-ink leading-tight">
            Pricing &amp; Monetization Plan
          </h1>
          <p className="text-[#6B6B6B] text-sm mt-2 max-w-2xl">
            What we charge, to whom, how, and <strong>why</strong>. The live SKU editor
            handles <em>amounts</em>; this doc handles <em>rationale</em>. Both are
            required to keep your mental model intact.
          </p>
          {updatedDate && (
            <div className="text-xs text-[#8a8a8a] mt-2" data-testid="pricing-ref-mtime">
              Last edited: {updatedDate}
              {doc?.line_count ? ` · ${doc.line_count} lines` : ""}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="reload-pricing-ref-btn"
          >
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            <span className="ml-1.5">Reload</span>
          </Button>
          <Link to="/admin/pricing">
            <Button
              size="sm"
              className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              data-testid="open-sku-editor-btn"
            >
              <Sliders size={14} weight="bold" className="mr-1.5" />
              Edit live SKUs
              <ArrowSquareOut size={12} weight="bold" className="ml-1.5 opacity-80" />
            </Button>
          </Link>
        </div>
      </header>

      {loading && !doc && (
        <div className="py-20 text-center text-[#6B6B6B]" data-testid="pricing-ref-loading">
          Loading pricing reference…
        </div>
      )}

      {doc && (
        <article
          className="pricing-ref-prose max-w-none"
          data-testid="pricing-ref-markdown"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}
